import type { User } from "firebase/auth";
import { loadGameArtForUser } from "@/shadow/merge-game-art";
import { equippedShadow, loadShadowState } from "@/shadow/firestore-storage";
import { shadowEquippedStatBonus } from "@/shadow/shadow-stat-bonus";
import { equipmentStatBonus, loadEquipmentState } from "@/character/equipment/storage";
import { loadCharacterProfile } from "@/character/storage";
import { hydrateDungeonFloorConfigs } from "@/dungeon/dungeon-floor-config";
import { loadDungeonMeta } from "@/dungeon/dungeon-meta";
import {
  buildDungeonPublishPayload,
  type DungeonFloorPublishSnapshot,
} from "@/dungeon/dungeon-publish";
import type { TowerArenaAmbienceConfig } from "@/tower/tower-arena-ambience";
import type { TowerFloorScenePlacement, TowerFloorSpawnRow } from "@/tower/tower-floor-config";
import { buildDungeonFloorBattlePlan } from "@/dungeon/dungeon-floor-runtime";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb } from "@/home/home-idb";
import { startTowerBattle } from "@/game/main";
import { equippedPet, loadPetState } from "@/pet/firestore-storage";
import { buildPetSkillRuntimePayload } from "@/pet/skills/runtime";
import {
  loadCharacterProgression,
  type CharacterProgression,
} from "@/game/model/character-progression";
import { mergeStatBonuses } from "@/game/model/character-stats";
import { loadMyRankCosmetics } from "@/rank/load-rank-state";
import { computeDungeonBattleRewards } from "@/dungeon/dungeon-battle-rewards";
import { showDungeonBattleResult } from "@/dungeon/dungeon-battle-result-panel";
import { bindDungeonBattleSurrender } from "@/dungeon/dungeon-battle-surrender";
import {
  mountTowerCombatOverlay,
  towerCombatBarMarkup,
  towerReviveModalMarkup,
  type TowerCombatOverlayHandle,
} from "@/tower/tower-combat-overlay";
import {
  TowerEvents,
  type TowerBattleEndedPayload,
  type TowerFloorClearedPayload,
} from "@/tower/tower-events";
import type Phaser from "phaser";

export type DungeonFloorTrialPreview = {
  floor: number;
  spawns: TowerFloorSpawnRow[];
  enabled: boolean;
  scenePlacements: TowerFloorScenePlacement[];
  sceneAmbience?: TowerArenaAmbienceConfig;
};

export type MountDungeonTrialPageOptions = {
  onRequestClose?: () => void;
  onBattleActiveChange?: (active: boolean) => void;
  /** ทดลองเฉพาะชั้นนี้ (ไม่เข้าบ้าน / ไม่รันชั้นอื่น) */
  onlyFloor?: number;
  /** ใช้ draft จากหน้าแก้ด่าน (ยังไม่กดบันทึก config) */
  floorTrialPreview?: DungeonFloorTrialPreview;
};

function applyFloorTrialPreview(
  floors: DungeonFloorPublishSnapshot[],
  preview: DungeonFloorTrialPreview,
): DungeonFloorPublishSnapshot[] {
  const idx = floors.findIndex((f) => f.floor === preview.floor);
  if (idx < 0) return floors;
  const out = [...floors];
  out[idx] = {
    ...out[idx],
    spawns: preview.spawns,
    enabled: preview.enabled,
    scenePlacements: preview.scenePlacements,
    ...(preview.sceneAmbience ? { sceneAmbience: preview.sceneAmbience } : {}),
  };
  return out;
}

type TrialPhase = "loading" | "fighting" | "summary" | "done";

type TrialRun = {
  floors: DungeonFloorPublishSnapshot[];
  maxFloors: number;
};

const GAME_ID = "dungeon-trial-game";

export async function mountDungeonTrialPage(
  root: HTMLElement,
  user: User,
  options?: MountDungeonTrialPageOptions,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  let progression: CharacterProgression = loadCharacterProgression(user.uid);
  let rankCosmetics = await loadMyRankCosmetics();
  const [shadowState, petState, economyFromIdb] = await Promise.all([
    loadShadowState(user.uid),
    loadPetState(user.uid),
    loadHomeEconomyFromIdb(user.uid),
  ]);
  const economy: HomeEconomy = economyFromIdb ?? createDefaultHomeEconomy();

  let trial: TrialRun | null = null;
  let floorIndex = 0;
  let phase: TrialPhase = "loading";
  let game: Phaser.Game | null = null;
  let closed = false;

  const equipmentBonus = () =>
    mergeStatBonuses(
      equipmentStatBonus(loadEquipmentState(user.uid)),
      shadowEquippedStatBonus(equippedShadow(shadowState)),
      rankCosmetics.statBonus,
    );

  const ensureShell = () => {
    if (root.querySelector(".tower-layout")) return;
    root.classList.add(
      "game-app",
      "page-raid",
      "page-tower",
      "dungeon-trial",
      "page-tower--battle",
    );
    root.innerHTML = `
      <div class="tower-layout">
        <div class="tower-stage">
          <p class="dungeon-trial-loading hint" id="dungeon-trial-loading">กำลังโหลดด่าน…</p>
          <div id="${GAME_ID}" class="tower-game-container home-game-container" hidden></div>
          ${towerCombatBarMarkup()}
        </div>
      </div>
      ${towerReviveModalMarkup()}
    `;
  };

  ensureShell();
  const combatOverlay: TowerCombatOverlayHandle = await mountTowerCombatOverlay(root, user, {
    isBattleActive: () => phase === "fighting",
    surrenderConfirm: "ออกจากการทดลองระหว่างต่อสู้?",
    persistEconomy: true,
    enableRevive: false,
  });

  const setLoadingVisible = (visible: boolean) => {
    const el = root.querySelector<HTMLElement>("#dungeon-trial-loading");
    if (el) el.hidden = !visible;
  };

  let syncSurrenderUi = () => {};

  const syncBattleChrome = () => {
    const inWorld = phase === "fighting";
    root.classList.toggle("page-tower--battle", inWorld);
    options?.onBattleActiveChange?.(inWorld);
    const container = root.querySelector<HTMLElement>(`#${GAME_ID}`);
    if (container) container.hidden = !inWorld;
    syncSurrenderUi();
  };

  const destroyGame = () => {
    game?.destroy(true);
    game = null;
    combatOverlay.onBattleEnd();
    syncBattleChrome();
  };

  const gameHost = (): HTMLElement => {
    const c = root.querySelector<HTMLElement>(`#${GAME_ID}`);
    if (!c) throw new Error("missing trial game container");
    return c;
  };

  const closeTrial = () => {
    if (closed) return;
    closed = true;
    phase = "done";
    trial = null;
    destroyGame();
    options?.onRequestClose?.();
  };

  const surrenderControl = bindDungeonBattleSurrender(root, {
    isBattleActive: () => phase === "fighting",
    confirmMessage: "ยอมแพ้และออกจากการทดลองด่าน?",
  });
  syncSurrenderUi = surrenderControl.sync;
  syncSurrenderUi();

  const failTrial = (message: string) => {
    if (closed) return;
    window.alert(message);
    closeTrial();
  };

  const mountGameInContainer = (boot: () => Phaser.Game) => {
    setLoadingVisible(false);
    const container = gameHost();
    container.hidden = false;
    container.innerHTML = "";
    game?.destroy(true);
    game = boot();
    requestAnimationFrame(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) {
        game?.scale.resize(w, h);
      } else {
        game?.scale.refresh();
      }
    });
    syncBattleChrome();
  };

  const startFloorBattle = async () => {
    if (!trial || closed) return;
    const snap = trial.floors[floorIndex];
    if (!snap) {
      closeTrial();
      return;
    }
    phase = "fighting";
    const plan = buildDungeonFloorBattlePlan(snap);
    const art = await loadGameArtForUser(user.uid, profile);
    const activePet = equippedPet(petState);
    combatOverlay.onBattleStart();
    mountGameInContainer(() =>
      startTowerBattle(gameHost(), art, {
        towerFloor: snap.floor,
        progression,
        equipmentStatBonus: equipmentBonus(),
        towerBattlePlan: plan,
        skillRuntime: combatOverlay.getSkillRuntime(),
        rankBadgeBoards: rankCosmetics.badgeBoards,
        rankBadgeConfigs: rankCosmetics.badgeConfigs,
        equippedPet: activePet,
        petSkillRuntime: activePet
          ? buildPetSkillRuntimePayload(activePet.skillBar, economy)
          : undefined,
      }),
    );
  };

  const resultHost = (): HTMLElement =>
    root.querySelector<HTMLElement>(".tower-stage") ?? root;

  const onFloorCleared = (event: Event) => {
    if (!trial || phase !== "fighting" || closed) return;
    const detail = (event as CustomEvent<TowerFloorClearedPayload>).detail;
    destroyGame();
    const hasMore =
      !options?.onlyFloor && floorIndex + 1 < trial.maxFloors;
    phase = "summary";
    syncBattleChrome();
    void (async () => {
      const rewards = computeDungeonBattleRewards({
        baseXp: detail.baseXp,
        trial: true,
        repeatClear: false,
      });
      const choice = await showDungeonBattleResult(resultHost(), {
        floor: detail.floor,
        victory: true,
        trial: true,
        baseXp: detail.baseXp,
        monstersKilled: detail.enemiesKilled ?? 0,
        rewards,
        primaryLabel: hasMore ? "ด่านถัดไป" : "เสร็จ",
        secondaryLabel: hasMore ? "ออก" : undefined,
      });
      if (closed || !trial) return;
      if (choice === "secondary" || !hasMore) {
        closeTrial();
        return;
      }
      floorIndex += 1;
      phase = "loading";
      setLoadingVisible(true);
      startFloorBattle();
    })();
  };

  const onBattleEnded = (event: Event) => {
    const detail = (event as CustomEvent<TowerBattleEndedPayload>).detail;
    if (detail.victory || phase !== "fighting" || closed) return;
    closeTrial();
  };

  const onBattleGiveUp = () => {
    if (phase !== "fighting" || closed) return;
    destroyGame();
    closeTrial();
  };

  window.addEventListener(TowerEvents.FloorCleared, onFloorCleared);
  window.addEventListener(TowerEvents.BattleEnded, onBattleEnded);
  window.addEventListener(TowerEvents.BattleGiveUp, onBattleGiveUp);

  try {
    const meta = loadDungeonMeta(user.uid);
    await hydrateDungeonFloorConfigs(user.uid);
    let floorSnapshots = (await buildDungeonPublishPayload(user, meta)).floors;
    if (options?.floorTrialPreview) {
      floorSnapshots = applyFloorTrialPreview(floorSnapshots, options.floorTrialPreview);
    }
    let active = floorSnapshots.filter(
      (s) => s.enabled && (s.spawns?.length ?? 0) > 0,
    );
    if (options?.onlyFloor) {
      active = active.filter((s) => s.floor === options.onlyFloor);
    }
    if (active.length === 0) {
      failTrial(
        options?.onlyFloor
          ? `ด่าน ${options.onlyFloor} ยังไม่พร้อมทดลอง — ตั้งมอนสเตอร์และเปิดใช้งาน`
          : "ยังไม่มีด่านพร้อมทดลอง — ตั้งมอนสเตอร์และเปิดใช้งานในแต่ละชั้นก่อน",
      );
    } else {
      trial = {
        floors: active,
        maxFloors: active.length,
      };
      floorIndex = 0;
      startFloorBattle();
    }
  } catch (err) {
    failTrial(err instanceof Error ? err.message : "โหลดด่านไม่สำเร็จ");
  }

  return () => {
    window.removeEventListener(TowerEvents.FloorCleared, onFloorCleared);
    window.removeEventListener(TowerEvents.BattleEnded, onBattleEnded);
    window.removeEventListener(TowerEvents.BattleGiveUp, onBattleGiveUp);
    surrenderControl.unbind();
    destroyGame();
    combatOverlay.destroy();
    options?.onBattleActiveChange?.(false);
    root.classList.remove("page-tower--battle");
  };
}
